/**
 * @file Level-lab state: the debug window's "Levels" tab.
 *
 * One live object, read by {@link ../world/dungeon/DungeonArena.js} at build
 * time — the same pattern the wave director uses for the sandbox toggles.
 * Nothing persists: the lab is a session tool for validating generation, not
 * a tuning layer.
 */

import { DUNGEON_PROPS } from './DungeonProps.js';
import { loadDebugPrefs, hydrate } from './DebugPrefs.js';

/** Every prop kind the lab can toggle, in catalog order. */
export const LAB_PROP_KEYS = Object.freeze(Object.keys(DUNGEON_PROPS));

/** Props that start disabled in the level generator's lab tuning. */
export const DEFAULT_DISABLED_PROPS = Object.freeze(['pillar', 'debris']);

const DEFAULT_DISABLED_PROP_SET = new Set(DEFAULT_DISABLED_PROPS);

/**
 * @param {string} key Prop kind.
 * @returns {boolean} Whether the prop starts enabled after a reset.
 */
export function defaultPropEnabled(key) {
  return !DEFAULT_DISABLED_PROP_SET.has(key);
}

/**
 * @returns {Record<string, boolean>} Fresh prop-enabled map.
 */
function defaultPropEnabledMap() {
  return Object.fromEntries(LAB_PROP_KEYS.map((k) => [k, defaultPropEnabled(k)]));
}

export const levelLab = {
  /** Forced theme key, or 'auto' for the per-floor rotation. */
  theme: 'auto',
  /**
   * Forced generation seed, or null to keep the run's own floor seed.
   * The Levels tab writes a visible random seed here so every generation
   * stays reproducible.
   * @type {number | null}
   */
  seed: null,
  /** Multiplies decor density and per-prop placement chances, 0..2. */
  densityMult: 1,
  /** @type {Record<string, boolean>} Prop kind -> enabled. */
  propEnabled: defaultPropEnabledMap()
};

// Survive vite reloads: a pinned map stays pinned across refreshes.
hydrate(loadDebugPrefs()?.lab, levelLab, ['theme', 'seed', 'densityMult'], ['propEnabled']);
for (const key of DEFAULT_DISABLED_PROPS) levelLab.propEnabled[key] = false;

/**
 * The asset-tuning snapshot the generator consumes.
 * @returns {{ disabled: Set<string>, densityMult: number, liquidPoolsEnabled: boolean }}
 */
export function labAssetTuning() {
  const disabled = new Set();
  for (const key of LAB_PROP_KEYS) if (!levelLab.propEnabled[key]) disabled.add(key);
  return { disabled, densityMult: levelLab.densityMult, liquidPoolsEnabled: false };
}

/** @returns {void} Resets every lab knob to its default. */
export function resetLevelLab() {
  levelLab.theme = 'auto';
  levelLab.seed = null;
  levelLab.densityMult = 1;
  for (const key of LAB_PROP_KEYS) levelLab.propEnabled[key] = defaultPropEnabled(key);
}
